import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { RoleToLinkMapper } from "../../utils/UrlUtils";
import { useGetRole } from "../../generic/hooks/useGetRole";
import { useRecoilValue } from "recoil";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../utils/configurationconstants";
import "../patientlist/patientlist.css"
import { Space, Tooltip } from "antd";
import { getFullName } from "../../careprovider/visits/utils";
import SmartIcon from "../../../components/generic/genericIcon/SmartIcon";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import MemoryType from "../../../service/memory/enum/MemoryType";
import apiService from "../../../service/apiservice/apiService";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import { performPostRequest } from "../../../service/network/NetworkService";
import { getApiUrl } from "../../../service/network/UrlMapper";
import IntentType from "../../../intentexecutor/utils/IntentType";

const TableColumns = () => {
	const user = useReadMemory(MemoryType.USER);
	const navigate = useNavigate();
	const role = useGetRole();
	const organizationIdentifier = useGetOrgIdentifier()
	const configuration = useRecoilValue(currentConfiguration);
	const [isViewVolunteerProfileAllowed, setIsVolunteerProfileAllowed] = useState(false);
	const [isViewAuditLogAllowed, setViewAuditLogAllowed] = useState(false);
	const [isViewCommunicationAllowed, setViewCommunicationAllowed] = useState(true);
	const [communicationLoading, setCommunicationLoading] = useState(false);
	const [alphabetizeByFamilyName, setAlphabetizeByFamilyName] = useState(false)

	useEffect(() => {

		const configurationViewVolunteerProfile = configuration[ConfigurationConstants.FEATUREACCESS_WORKFORCELIST_VIEW_VOLUNTEER_PROFILE];
		setIsVolunteerProfileAllowed(configurationViewVolunteerProfile?.visibility);

		const configurationViewAuditLogs = configuration[ConfigurationConstants.FEATUREACCESS_PATIENTLIST_VIEW_AUDIT_LOGS];
        setViewAuditLogAllowed(configurationViewAuditLogs?.visibility);

		const configurationViewCommunication = configuration[ConfigurationConstants.FEATUREACCESS_WORKFORCELIST_VIEW_COMMUNICATION];
        setViewCommunicationAllowed(configurationViewCommunication?.visibility);

	}, [configuration]);

	useEffect(() => {
		const getAlphabetizeConfig = async () => {
			const payload = {
				intent: "get_configuration",
				payload: { configurationKey: ["niramaya.alphabetize.workforcelist"] },
				context: { organizationIdentifier }
			};

			const result = await performPostRequest(getApiUrl()[IntentType.GET_CONFIGURATION], JSON.stringify(payload));
			const response = await result.json();
			const alphabetizeConfig = JSON.parse(response[0]?.configurationValue || "{}");

			setAlphabetizeByFamilyName(alphabetizeConfig?.alphabetizeByFamilyName);
		};

		getAlphabetizeConfig();
	}, [organizationIdentifier]);

	const handleProfileView = (rowClicked) => {
		navigate(RoleToLinkMapper(role) + "volunteer-profile?identifier=" + rowClicked["userIdentifier"]);
	};

	const handleAuditLogsView = (rowClicked) => {
        navigate(RoleToLinkMapper(role) + "audit-logs?workforce=" + rowClicked["userIdentifier"]);
    };

	const handleCommunication = async (record) => {
		if (communicationLoading) return;

		if (record?.roomId) {
			navigate(`${RoleToLinkMapper(role)}communication?roomId=${record?.roomId}`);
			return;
		}

		const payload = {
			organizationIdentifier: user?.organizationIdentifier,
			chatType: "direct",
			title: null,
			createdBy: user?.userIdentifier,
			memberUserIdentifiers: [record?.userIdentifier]
		};

		setCommunicationLoading(true);
		try {
			const result = await apiService.post(
				"niramayafoundation/api/v1/communication/room",
				payload,
				"Chat room ready",
				"Could not start communication",
			);

			if (!result) return;
			navigate(`${RoleToLinkMapper(role)}communication?roomId=${result?.roomId}`);
		} finally {
			setCommunicationLoading(false);
		}
	};

	const columns = [
		{
			title: "Workforce Name",
			dataIndex: "givenName",
			sorter: {
				compare: (a, b) => alphabetizeByFamilyName ? a.familyName.localeCompare(b.familyName) : a.givenName.localeCompare(b.givenName),
			},
			render: (text, record) => <span>{getFullName(record?.givenName, record?.familyName)}</span>,
		},
		{
			title: "Job Title",
			dataIndex: "jobTitle",
			sorter: {
				compare: (a, b) => a.jobTitle.localeCompare(b.jobTitle),
				multiple: 3,
			},
		},
		{
			title: "Actions",
			dataIndex: "action",
			render: (text, record) => (
				<>
					<Space size={20} className="patientlist-action-items">
						{isViewVolunteerProfileAllowed &&
							<Tooltip title="View profile" placement="bottom" >
								<>
									<SmartIcon iconKey={"profile"} handleIconClick={() => handleProfileView(record)} />
								</>
							</Tooltip>
						}
						{isViewAuditLogAllowed &&
							<Tooltip title="View audit logs" placement="bottom" >
								<>
									<SmartIcon iconKey={"audit-logs"} handleIconClick={() => handleAuditLogsView(record)} />
								</>
							</Tooltip>
						}
						{isViewCommunicationAllowed &&
							<Tooltip title={communicationLoading ? "Opening chat…" : "Communication"} placement="bottom" >
								<span
									style={{
										opacity: communicationLoading ? 0.55 : 1,
										pointerEvents: communicationLoading ? "none" : "auto",
										display: "inline-flex",
									}}
								>
									<SmartIcon iconKey={"communication"} handleIconClick={() => handleCommunication(record)} />
								</span>
							</Tooltip>
						}
					</Space>

				</>
			),
		},
	];

	return { columns };
};

export default TableColumns;
